import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Chip,
  Grid
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import SecurityIcon from "@mui/icons-material/Security";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SaveIcon from "@mui/icons-material/Save";
import { Application } from "@microsoft/microsoft-graph-types";
import EnhancedEncryptionIcon from '@mui/icons-material/EnhancedEncryption';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import AssignmentIcon from '@mui/icons-material/Assignment';
import Divider from '@mui/material/Divider';
import TokenIcon from '@mui/icons-material/Token';
import ListIcon from '@mui/icons-material/List';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';

/**
 * Props: 
 *  - modifiedApp: Current Application object in local state
 *  - setModifiedApp: Setter function to modify Application in parent
 *  - updateApplication: Callback to push changes to Entra
 */
interface OptionalClaimsProps {
  modifiedApp: Application;
  setModifiedApp: React.Dispatch<React.SetStateAction<Application>>;
  updateApplication: () => void;
  claimsMappingPolicy?: any; // New prop
}

/** Example known claims for user reference. Adjust as you wish. */
const KNOWN_OPTIONAL_CLAIMS = [
  {
    name: "email",
    recommendedPermissions: ["email"],
    docsLink:
      "https://learn.microsoft.com/azure/active-directory/develop/active-directory-optional-claims#email",
  },
  {
    name: "family_name",
    recommendedPermissions: ["profile"],
    docsLink:
      "https://learn.microsoft.com/azure/active-directory/develop/active-directory-optional-claims#family_name",
  },
  {
    name: "given_name",
    recommendedPermissions: ["profile"],
    docsLink:
      "https://learn.microsoft.com/azure/active-directory/develop/active-directory-optional-claims#given_name",
  },
  {
    name: "upn",
    recommendedPermissions: ["User.Read"],
    docsLink:
      "https://learn.microsoft.com/azure/active-directory/develop/active-directory-optional-claims#upn",
  },
];

/** The OptionalClaims component */
const OptionalClaims: React.FC<OptionalClaimsProps> = ({
  modifiedApp,
  setModifiedApp,
  updateApplication,
  claimsMappingPolicy,
}) => {
  // Local state for adding a new claim
  const [selectedClaimName, setSelectedClaimName] = useState<string>("");
  const [selectedTokenType, setSelectedTokenType] = useState<
    "accessToken" | "idToken" | "saml2Token"
  >("accessToken");
  const [isClaimEssential, setIsClaimEssential] = useState<boolean>(false);
  // New state variable for toggling explanation
  const [showClaimsExplanation, setShowClaimsExplanation] = useState<boolean>(false);
  const [showSaveReminder, setShowSaveReminder] = useState<boolean>(false);
  const [showRemoveReminder, setShowRemoveReminder] = useState<boolean>(false);
  const [optionalClaimsExpanded, setOptionalClaimsExpanded] = useState<boolean>(true);
  const [additionalConfigExpanded, setAdditionalConfigExpanded] = useState<boolean>(false);

  // Find any helpful data for the chosen claim
  const selectedClaimObj = KNOWN_OPTIONAL_CLAIMS.find(
    (c) => c.name === selectedClaimName
  );

  /** Handler: add a new claim to modifiedApp */
  const handleAddClaim = () => {
    if (!selectedClaimName) return;

    // build new claim object
    const newClaim = {
      name: selectedClaimName,
      essential: isClaimEssential,
    };

    setModifiedApp((prev) => {
      const existingOptional = prev.optionalClaims || {};
      // the array for the selected token type
      const updatedArray = [
        ...(existingOptional[selectedTokenType] || []),
        newClaim,
      ];
      return {
        ...prev,
        optionalClaims: {
          ...existingOptional,
          [selectedTokenType]: updatedArray,
        },
      };
    });

    // Show the save reminder
    setShowSaveReminder(true);
    
    // Optionally hide it after a delay
    setTimeout(() => {
      setShowSaveReminder(false);
    }, 6000); // Hide after 6 seconds

    // Clear local fields
    setSelectedClaimName("");
    setIsClaimEssential(false);
  };

  // Handlers for parent accordions
  const handleOptionalClaimsChange = (event: React.SyntheticEvent, expanded: boolean) => {
    setOptionalClaimsExpanded(expanded);
  };

  const handleAdditionalConfigChange = (event: React.SyntheticEvent, expanded: boolean) => {
    setAdditionalConfigExpanded(expanded);
  };

  /** Handler: remove an existing claim from whichever token type */
  const handleRemoveOptionalClaim = (
    tokenType: "accessToken" | "idToken" | "saml2Token",
    index: number
  ) => {
    setModifiedApp((prev) => {
      const claimsArray = [...(prev.optionalClaims?.[tokenType] || [])];
      claimsArray.splice(index, 1);
      return {
        ...prev,
        optionalClaims: {
          ...prev.optionalClaims,
          [tokenType]: claimsArray,
        },
      };
    });

    // Show removal reminder
    setShowRemoveReminder(true);
    
    // Hide it after a delay
    setTimeout(() => {
      setShowRemoveReminder(false);
    }, 6000); // Hide after 6 seconds  
  };

  return (
    <Card variant="outlined" sx={{ mb: 4 }}>
      <CardContent>
        {/* Main Title for the entire Token Configuration section */}
        {/* <Typography 
          variant="h6" 
          sx={{ 
            fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
            fontSize: "1.3rem",
            fontWeight: 600,
            color: "primary.main",
            letterSpacing: "0.01em",
            mb: 3
          }}
        >
          Token Configuration
        </Typography> */}

        {/* OPTIONAL CLAIMS SECTION - Updated with better visual structure */}
        <Accordion 
          expanded={optionalClaimsExpanded} 
          onChange={handleOptionalClaimsChange}
          sx={{
            border: '1px solid #e0e7ff', 
            borderRadius: '8px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            mb: 3
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{ 
              background: 'linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%)',
              borderBottom: optionalClaimsExpanded ? '1px solid #e0e7ff' : 'none',
              minHeight: '64px'
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              width: '100%',
            }}>
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: '#3f51b5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2
              }}>
                <TokenIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
              </Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                  fontSize: "1.25rem",
                  fontWeight: 600,
                  color: "#1a237e",
                  letterSpacing: "0.02em",
                  flexGrow: 1
                }}
              >
                Optional Claims
              </Typography>
            </Box>
          </AccordionSummary>

          <AccordionDetails sx={{ p: 0 }}>
            <Box sx={{ backgroundColor: '#fafbff' }}>

              {/* 1) EXISTING Optional Claims - Enhanced Card Design */}
              <Box sx={{ 
                m: 2, 
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)'
              }}>
                <Accordion 
                  sx={{
                    '&:before': { display: 'none' },
                    borderRadius: '12px !important'
                  }}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      background: 'linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)',
                      borderBottom: '1px solid #c8e6c9',
                      minHeight: '56px'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '8px',
                        backgroundColor: '#4caf50',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1.5
                        }}>
                        <ListIcon sx={{ color: 'white', fontSize: '0.85rem' }} />
                        </Box>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontSize: "1.1rem",
                          fontWeight: 600,
                          color: "#2e7d32",
                          letterSpacing: "0.01em"
                        }}
                      >
                        Configured Optional Claims
                      </Typography>
                      {/* Add count badge */}
                      <Chip 
                        size="small"
                        label={`${(modifiedApp.optionalClaims?.accessToken?.length || 0) + 
                                (modifiedApp.optionalClaims?.idToken?.length || 0) + 
                                (modifiedApp.optionalClaims?.saml2Token?.length || 0)} claims`}
                        sx={{ 
                          ml: 2,
                          backgroundColor: '#c8e6c9',
                          color: '#2e7d32',
                          fontWeight: 500
                        }}
                      />
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Box sx={{ p: 3, backgroundColor: "#fafffe" }}>
                      {/* Enhanced empty state */}
                      {(!modifiedApp.optionalClaims?.accessToken?.length && 
                        !modifiedApp.optionalClaims?.idToken?.length && 
                        !modifiedApp.optionalClaims?.saml2Token?.length) ? (
                        <Box sx={{ 
                          p: 4, 
                          backgroundColor: '#f8f9fa', 
                          borderRadius: '12px', 
                          textAlign: 'center',
                          border: '2px dashed #dee2e6'
                        }}>
                          <Box sx={{
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            backgroundColor: '#e9ecef',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 2
                          }}>
                            <TokenIcon sx={{ fontSize: '2rem', color: '#6c757d' }} />
                          </Box>
                          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                            No optional claims configured
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Add claims using the section below to customize your tokens
                          </Typography>
                        </Box>
                      ) : (
                        // Enhanced claims display with better token type separation
                        <Stack spacing={3}>
                          {['accessToken', 'idToken', 'saml2Token'].map((tokenType) => {
                            const claims = modifiedApp.optionalClaims?.[tokenType as keyof typeof modifiedApp.optionalClaims] || [];
                            if (!claims.length) return null;
                            
                            const tokenTypeLabels = {
                              accessToken: 'Access Token',
                              idToken: 'ID Token', 
                              saml2Token: 'SAML2 Token'
                            };
                            
                            const tokenTypeColors = {
                              accessToken: '#1976d2',
                              idToken: '#7b1fa2',
                              saml2Token: '#f57c00'
                            };

                            return (
                              <Box key={tokenType}>
                                <Box sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  mb: 2,
                                  pb: 1,
                                  borderBottom: `2px solid ${tokenTypeColors[tokenType as keyof typeof tokenTypeColors]}20`
                                }}>
                                  <Box sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: '4px',
                                    backgroundColor: tokenTypeColors[tokenType as keyof typeof tokenTypeColors],
                                    mr: 1.5
                                  }} />
                                  <Typography 
                                    variant="subtitle1" 
                                    sx={{ 
                                      fontWeight: 600,
                                      color: tokenTypeColors[tokenType as keyof typeof tokenTypeColors],
                                      fontSize: '1.1rem'
                                    }}
                                  >
                                    {tokenTypeLabels[tokenType as keyof typeof tokenTypeLabels]} Optional Claims
                                  </Typography>
                                  <Chip 
                                    size="small"
                                    label={`${claims.length} claims`}
                                    sx={{ 
                                      ml: 'auto',
                                      backgroundColor: `${tokenTypeColors[tokenType as keyof typeof tokenTypeColors]}15`,
                                      color: tokenTypeColors[tokenType as keyof typeof tokenTypeColors]
                                    }}
                                  />
                                </Box>
                                
                                <List sx={{ 
                                  bgcolor: "#ffffff", 
                                  border: `1px solid ${tokenTypeColors[tokenType as keyof typeof tokenTypeColors]}30`, 
                                  borderRadius: '8px',
                                  overflow: 'hidden'
                                }}>
                                  {claims.map((claim: any, index: number) => (
                                    <ListItem
                                      key={`${tokenType}-${index}`}
                                      sx={{
                                        borderBottom: index < claims.length - 1 ? "1px solid #f5f5f5" : "none",
                                        '&:hover': { backgroundColor: '#fafafa' }
                                      }}
                                      secondaryAction={
                                        <Tooltip title="Remove claim">
                                          <IconButton
                                            edge="end"
                                            color="error"
                                            onClick={() => handleRemoveOptionalClaim(tokenType as any, index)}
                                            sx={{
                                              '&:hover': {
                                                backgroundColor: '#ffebee'
                                              }
                                            }}
                                          >
                                            <DeleteIcon />
                                          </IconButton>
                                        </Tooltip>
                                      }
                                    >
                                      <ListItemText
                                        primary={
                                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography variant="body1" fontWeight="medium" sx={{ mr: 1 }}>
                                              {claim.name}
                                            </Typography>
                                            {claim.essential && (
                                              <Chip 
                                                size="small" 
                                                label="Essential" 
                                                color="warning"
                                                variant="outlined"
                                                sx={{ height: '20px', fontSize: '0.7rem' }}
                                              />
                                            )}
                                          </Box>
                                        }
                                        secondary={`Required: ${claim.essential ? "Yes" : "No"}`}
                                      />
                                    </ListItem>
                                  ))}
                                </List>
                              </Box>
                            );
                          })}
                        </Stack>
                      )}
                      
                      {/* Apply Changes Button for Removal - Enhanced */}
                      {showRemoveReminder && (
                        <Box sx={{ 
                          mt: 3,
                          p: 2,
                          backgroundColor: '#fff3e0',
                          borderRadius: '8px',
                          border: '1px solid #ffcc02'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <WarningIcon sx={{ color: '#f57c00', mr: 1 }} />
                            <Typography variant="body2" fontWeight="medium">
                              Changes pending - click Apply Changes to save
                            </Typography>
                          </Box>
                          <Button
                            variant="contained"
                            color="success"
                            startIcon={<SaveIcon />}
                            onClick={() => {
                              updateApplication();
                              setShowRemoveReminder(false);
                            }}
                            sx={{ borderRadius: '8px' }}
                          >
                            Apply Changes
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>

              {/* 2) ADD NEW Optional Claims - Enhanced Card Design */}
              <Box sx={{ 
                m: 2, 
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)'
              }}>
                <Accordion sx={{
                  '&:before': { display: 'none' },
                  borderRadius: '12px !important'
                }}>
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      background: 'linear-gradient(135deg, #e3f2fd 0%, #f0f8ff 100%)',
                      borderBottom: '1px solid #bbdefb',
                      minHeight: '56px'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '8px',
                        backgroundColor: '#2196f3',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1.5
                      }}>
                        <AddIcon sx={{ color: 'white', fontSize: '1rem' }} />
                      </Box>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontSize: "1.1rem",
                          fontWeight: 600,
                          color: "#1565c0",
                          letterSpacing: "0.01em"
                        }}
                      >
                        New Optional Claim
                      </Typography>
                      <Chip 
                        size="small"
                        label="Configure" 
                        sx={{ 
                          ml: 2,
                          backgroundColor: '#bbdefb',
                          color: '#1565c0',
                          fontWeight: 500
                        }}
                      />
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Box sx={{ p: 3, backgroundColor: "#f8fbff" }}>
                      {/* Enhanced form with better visual grouping */}
                      <Stack spacing={3}>
                        <Box sx={{
                          p: 3,
                          backgroundColor: 'white',
                          borderRadius: '12px',
                          border: '1px solid #e3f2fd'
                        }}>
                          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#1565c0' }}>
                            Claim Configuration
                          </Typography>
                          
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                                Token Type
                              </Typography>
                              <TextField
                                select
                                fullWidth
                                value={selectedTokenType}
                                onChange={(e) => setSelectedTokenType(e.target.value as "accessToken" | "idToken" | "saml2Token")}
                                variant="outlined"
                                size="small"
                                sx={{ 
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px'
                                  }
                                }}
                              >
                                <MenuItem value="accessToken">
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{ width: 12, height: 12, borderRadius: '2px', backgroundColor: '#1976d2', mr: 1 }} />
                                    Access Token
                                  </Box>
                                </MenuItem>
                                <MenuItem value="idToken">
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{ width: 12, height: 12, borderRadius: '2px', backgroundColor: '#7b1fa2', mr: 1 }} />
                                    ID Token
                                  </Box>
                                </MenuItem>
                                <MenuItem value="saml2Token">
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{ width: 12, height: 12, borderRadius: '2px', backgroundColor: '#f57c00', mr: 1 }} />
                                    SAML2 Token
                                  </Box>
                                </MenuItem>
                              </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                                Claim Name
                              </Typography>
                              <TextField
                                select
                                fullWidth
                                value={selectedClaimName}
                                onChange={(e) => setSelectedClaimName(e.target.value)}
                                variant="outlined"
                                size="small"
                                sx={{ 
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px'
                                  }
                                }}
                              >
                                <MenuItem value="">-- Select a Claim --</MenuItem>
                                {KNOWN_OPTIONAL_CLAIMS.map((c) => (
                                  <MenuItem key={c.name} value={c.name}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                      <Typography>{c.name}</Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {c.recommendedPermissions.join(', ')}
                                      </Typography>
                                    </Box>
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid>

                            <Grid item xs={12} sm={2}>
                              <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                                &nbsp;
                              </Typography>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={isClaimEssential}
                                    onChange={(e) => setIsClaimEssential(e.target.checked)}
                                    color="warning"
                                  />
                                }
                                label="Essential"
                                sx={{ 
                                  height: '40px',
                                  '& .MuiFormControlLabel-label': {
                                    fontSize: '0.875rem',
                                    fontWeight: 500
                                  }
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Box>

                        {/* Enhanced recommendation display */}
                        {selectedClaimName && selectedClaimObj && (
                          <Box sx={{ 
                            p: 2.5, 
                            backgroundColor: '#e8f5e9', 
                            borderLeft: '4px solid #4caf50',
                            borderRadius: '0 8px 8px 0',
                            border: '1px solid #c8e6c9'
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                              <InfoIcon sx={{ color: '#4caf50', mr: 1.5, mt: 0.25 }} />
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                  Claim: {selectedClaimObj.name}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                  Requires permissions: <strong>{selectedClaimObj.recommendedPermissions.join(", ")}</strong>
                                </Typography>
                                <Link
                                  href={selectedClaimObj.docsLink}
                                  target="_blank"
                                  rel="noopener"
                                  sx={{ fontSize: '0.875rem', fontWeight: 500 }}
                                >
                                  View documentation →
                                </Link>
                              </Box>
                            </Box>
                          </Box>
                        )}

                        {/* Action buttons with enhanced styling */}
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          pt: 2,
                          borderTop: '1px solid #e3f2fd'
                        }}>
                          <Button 
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={handleAddClaim}
                            disabled={!selectedClaimName}
                            sx={{ 
                              borderRadius: '8px',
                              textTransform: 'none',
                              fontWeight: 500
                            }}
                          >
                            Add Claim
                          </Button>
                          
                          <Button
                            variant="contained"
                            color="success"
                            startIcon={<SaveIcon />}
                            onClick={() => {
                              updateApplication();
                              setShowSaveReminder(false);
                            }}
                            sx={{ 
                              borderRadius: '8px',
                              textTransform: 'none',
                              fontWeight: 500
                            }}
                          >
                            Apply Changes
                          </Button>
                        </Box>
                      </Stack>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>

              {/* 3) INFORMATION ACCORDION - De-emphasized Design with consistent styling */}
              <Box sx={{ 
                m: 2, 
                borderRadius: '8px', // Smaller radius
                overflow: 'hidden',
                boxShadow: '0 1px 4px rgba(0, 0, 0, 0.04)', // Much lighter shadow
                opacity: 0.85 // Slight transparency to de-emphasize
              }}>
                <Accordion sx={{
                  '&:before': { display: 'none' },
                  borderRadius: '8px !important'
                }}>
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ fontSize: '1.2rem', color: '#9e9e9e' }} />} // Smaller, grayed icon
                    sx={{
                      background: '#f8f9fa', // Simple gray background instead of gradient
                      borderBottom: '1px solid #e9ecef',
                      minHeight: '48px', // Shorter height
                      '&:hover': {
                        backgroundColor: '#f1f3f4' // Subtle hover effect
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <InfoOutlinedIcon sx={{ mr: 1.5, fontSize: "1.1rem", color: 'info.main' }} /> {/* Updated to match Claims Mapping Policy styling */}
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                          fontSize: "1rem", 
                          fontWeight: 500, 
                          letterSpacing: "0.005em"
                        }}
                      >
                        About Optional Claims
                      </Typography>
                      <Chip 
                        size="small"
                        label="Info" 
                        variant="outlined" 
                        sx={{ 
                          ml: 2,
                          borderColor: '#dee2e6',
                          color: '#6c757d',
                          fontSize: '0.65rem', 
                          height: '20px', 
                          fontWeight: 400
                        }}
                      />
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Box sx={{ p: 2, backgroundColor: "#f5f5ff", borderRadius: 1 }}>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        Optional claims allow you to include additional user attributes in tokens that wouldn't normally be included.
                      </Typography>
                      
                      {/* Visual explanation of claims */}
                      <Box sx={{ 
                        p: 2, 
                        backgroundColor: '#ffffff', 
                        border: '1px solid #e0e0ff', 
                        borderRadius: 1,
                        mb: 2
                      }}>
                        <Typography variant="subtitle2" sx={{ color: '#0d47a1', mb: 1 }}>
                          Understanding Token Types:
                        </Typography>
                        <Typography variant="body2">
                          • <strong>Access Tokens</strong>: Used to access protected resources and APIs. Claims added here are available to API endpoints.<br />
                          • <strong>ID Tokens</strong>: Used for authentication and contain user identity information. Claims here are available to your client application.<br />
                          • <strong>SAML2 Tokens</strong>: Used for legacy applications that use SAML protocol instead of OAuth/OIDC.
                        </Typography>
                      </Box>
                      
                      <Box sx={{
                        p: 2,
                        backgroundColor: '#fff8e1',
                        border: '1px solid #ffe082',
                        borderRadius: 1
                      }}>
                        <Typography variant="subtitle2" sx={{ color: '#bf360c', mb: 1 }}>
                          Important Notes:
                        </Typography>
                        <Typography variant="body2">
                          • <strong>Essential claims</strong> are marked as required, but some identity providers may still omit them if unavailable.<br />
                          • Some claims require specific permissions to be configured in the API Permissions section.<br />
                          • Changes to claims require user consent again if users have previously consented to your application.
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionDetails>

                </Accordion>
              </Box>

            </Box>
          </AccordionDetails>
        </Accordion>

        {/* 2) ADDITIONAL TOKEN CONFIGURATION - Updated to match Optional Claims styling */}
        <Accordion 
          defaultExpanded={false} // Collapsed by default
          sx={{
            border: '1px solid #e0e0ff', 
            borderRadius: '8px !important', // Updated to match Optional Claims
            '&:before': { display: 'none' }, 
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)', // Updated to match Optional Claims
            mb: 3 // Updated to match Optional Claims
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{ 
              background: 'linear-gradient(135deg, #f3f6ff 0%, #e8eaf6 100%)', // Updated gradient to match style
              borderBottom: additionalConfigExpanded ? '1px solid #e0e0ff' : 'none', // Conditional border like Optional Claims
              minHeight: '64px' // Updated to match Optional Claims
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              width: '100%',
            }}>
              <Box sx={{
                width: 40, // Updated to match Optional Claims
                height: 40, // Updated to match Optional Claims
                borderRadius: '50%', // Updated to match Optional Claims
                backgroundColor: '#3f51b5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2 // Updated to match Optional Claims
              }}>
                <EnhancedEncryptionIcon sx={{ color: 'white', fontSize: '1.2rem' }} /> {/* Updated size to match */}
              </Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                  fontSize: "1.25rem", // Updated to match Optional Claims
                  fontWeight: 600,
                  color: "#1a237e", // Updated to match Optional Claims color scheme
                  letterSpacing: "0.02em", // Updated to match Optional Claims
                  flexGrow: 1
                }}
              >
                Additional Token Configuration
              </Typography>
              <Chip 
                size="small"
                label="Advanced" 
                sx={{ 
                  backgroundColor: '#e3f2fd', 
                  color: '#1565c0',
                  fontWeight: 500,
                  border: '1px solid #bbdefb',
                  mr: 1
                }}
              />
            </Box>
          </AccordionSummary>
          
          <AccordionDetails sx={{ 
            p: 0, 
            backgroundColor: '#fafbff' // Updated to match Optional Claims
          }}>

            {/* Updated sub-accordions to match the card-based design */}
            <Box sx={{ backgroundColor: '#fafbff' }}>

              {/* 1) Claims Mapping Policy Section - Enhanced Card Design */}
              <Box sx={{ 
                m: 2, 
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)'
              }}>
                {claimsMappingPolicy ? (
                  <Accordion defaultExpanded sx={{
                    '&:before': { display: 'none' },
                    borderRadius: '12px !important'
                  }}>
                    <AccordionSummary 
                      expandIcon={<ExpandMoreIcon />} 
                      sx={{
                        background: 'linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)', // Green theme for assigned policy
                        borderBottom: '1px solid #c8e6c9',
                        minHeight: '56px'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{
                          width: 28, // Updated to match sub-accordion styling
                          height: 28,
                          borderRadius: '8px',
                          backgroundColor: '#4caf50',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 1.5
                        }}>
                          <AssignmentIcon sx={{ color: 'white', fontSize: '0.85rem' }} /> {/* Updated size to match */}
                        </Box>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontSize: "1.1rem", // Updated to match sub-accordion styling
                            fontWeight: 600,
                            color: "#2e7d32",
                            letterSpacing: "0.01em"
                          }}
                        >
                          Assigned Claims Mapping Policy
                        </Typography>
                        <Chip 
                          size="small"
                          label="Active" 
                          sx={{ 
                            ml: 2,
                            backgroundColor: '#c8e6c9',
                            color: '#2e7d32',
                            fontWeight: 500
                          }}
                        />
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails>
                      <Box sx={{ p: 3, backgroundColor: "#fafffe" }}>
                        {/* Policy details content remains the same but with updated container styling */}
                        <Box sx={{ 
                          mb: 2, 
                          p: 2, 
                          backgroundColor: 'white', 
                          border: '1px solid #e0e0e0', 
                          borderRadius: '8px' // Updated border radius
                        }}>
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            alignItems: 'flex-start' 
                          }}>
                            <Box>
                              <Typography variant="subtitle1" sx={{ fontSize: '1.15rem', fontWeight: 500, mb: 0.5 }}>
                                {claimsMappingPolicy.displayName}
                              </Typography>
                              {claimsMappingPolicy.isOrganizationDefault && (
                                <Chip 
                                  size="small" 
                                  label="Default" 
                                  color="primary" 
                                  sx={{ mb: 1 }} 
                                />
                              )}
                              {claimsMappingPolicy.description && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                  {claimsMappingPolicy.description}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Box>
                        
                        {/* Rest of policy content remains the same */}
                        {claimsMappingPolicy.parsedDefinition?.ClaimsMappingPolicy?.ClaimsSchema?.length > 0 ? (
                          <>
                            <Box sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: 1.5
                            }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                <AccountBoxIcon sx={{ 
                                  fontSize: '1.2rem', 
                                  verticalAlign: 'middle', 
                                  mr: 1, 
                                  color: '#4caf50' // Updated to match green theme
                                }} />
                                Custom Claims ({claimsMappingPolicy.parsedDefinition.ClaimsMappingPolicy.ClaimsSchema.length})
                              </Typography>
                              <Chip 
                                size="small" 
                                label="Managed by policy" 
                                color="success" // Updated to match green theme
                                variant="outlined"
                              />
                            </Box>
                            
                            {/* Claims list styling remains the same */}
                            <List sx={{ 
                              bgcolor: "#ffffff", 
                              border: "1px solid #e0e0e0", 
                              borderRadius: '8px', // Updated border radius
                              mb: 2,
                              overflow: 'hidden'
                            }}>
                              {claimsMappingPolicy.parsedDefinition.ClaimsMappingPolicy.ClaimsSchema.map((claim: any, index: number) => {
                                const isJwtClaim = !!claim.JwtClaimType;
                                const claimType = isJwtClaim ? claim.JwtClaimType : claim.SamlClaimType || 'Unnamed Claim';
                                const claimSource = claim.Source || 'Unknown';
                                const claimId = claim.ID || 'Unknown ID';
                                
                                return (
                                  <ListItem
                                    key={`claim-${index}`}
                                    sx={{
                                      borderBottom: 
                                        index < claimsMappingPolicy.parsedDefinition.ClaimsMappingPolicy.ClaimsSchema.length - 1 
                                          ? "1px solid #f0f0f0" 
                                          : "none",
                                      backgroundColor: index % 2 === 0 ? '#fafafa' : 'white'
                                    }}
                                  >
                                    <ListItemText
                                      primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                          <Typography 
                                            variant="body2" 
                                            component="span" 
                                            fontWeight="medium" 
                                            sx={{ mr: 1, fontFamily: 'monospace', fontSize: '0.9rem' }}
                                          >
                                            {claimType}
                                          </Typography>
                                          <Chip 
                                            size="small" 
                                            label={isJwtClaim ? "JWT" : "SAML"}
                                            color={isJwtClaim ? "primary" : "secondary"}
                                            variant="outlined"
                                            sx={{ height: 20, fontSize: '0.65rem' }}
                                          />
                                        </Box>
                                      }
                                      secondary={
                                        <Box component="span">
                                          <Box sx={{ 
                                            display: 'grid', 
                                            gridTemplateColumns: { xs: '1fr', sm: '80px 1fr' },
                                            gap: 0.5,
                                            mt: 0.5
                                          }}>
                                            <Typography variant="caption" component="span" color="text.secondary">
                                              Source:
                                            </Typography>
                                            <Typography variant="caption" component="span" sx={{ fontWeight: 500 }}>
                                              {claimSource}
                                            </Typography>
                                            
                                            <Typography variant="caption" component="span" color="text.secondary">
                                              ID:
                                            </Typography>
                                            <Typography variant="caption" component="span" sx={{ fontFamily: 'monospace' }}>
                                              {claimId}
                                            </Typography>
                                            
                                            {claim.DefaultValue && (
                                              <>
                                                <Typography variant="caption" component="span" color="text.secondary">
                                                  Default:
                                                </Typography>
                                                <Typography variant="caption" component="span">
                                                  {claim.DefaultValue}
                                                </Typography>
                                              </>
                                            )}
                                          </Box>
                                        </Box>
                                      }
                                    />
                                  </ListItem>
                                );
                              })}
                            </List>
                          </>
                        ) : (
                          <Typography variant="body2" sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: '8px' }}>
                            This policy doesn't define any custom claims schema.
                          </Typography>
                        )}
                        
                        {/* Include basic claim set info styling updated */}
                        {claimsMappingPolicy.parsedDefinition?.ClaimsMappingPolicy?.IncludeBasicClaimSet && (
                          <Box sx={{ 
                            mt: 2, 
                            p: 2, 
                            backgroundColor: '#e8f5e9', // Updated to match green theme
                            borderRadius: '8px', // Updated border radius
                            border: '1px solid #c8e6c9', // Updated to match green theme
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}>
                            <InfoOutlinedIcon sx={{ color: '#4caf50' }} /> {/* Updated to match green theme */}
                            <Box>
                              <Typography variant="subtitle2" sx={{ color: '#2e7d32' }}> {/* Updated to match green theme */}
                                Basic Claim Set Included
                              </Typography>
                              <Typography variant="body2">
                                Standard claims like name, tid, and user identity are automatically included in tokens.
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ) : (
                  <Accordion sx={{
                    '&:before': { display: 'none' },
                    borderRadius: '12px !important'
                  }}>
                    <AccordionSummary 
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        background: 'linear-gradient(135deg, #e8eaf6 0%, #f3e5f5 100%)', // Light indigo/purple gradient
                        borderBottom: '1px solid #c5cae9',
                        minHeight: '56px'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '8px',
                          backgroundColor: '#3f51b5', // Indigo background
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 1.5
                        }}>
                          <AssignmentIcon sx={{ color: 'white', fontSize: '0.85rem' }} />
                        </Box>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontSize: "1.1rem",
                            fontWeight: 600,
                            color: "#283593", // Darker indigo text
                            letterSpacing: "0.01em"
                          }}
                        >
                          Policy Assignment Status
                        </Typography>
                        <Chip 
                          size="small"
                          label="None" 
                          sx={{ 
                            ml: 2,
                            backgroundColor: '#c5cae9', // Light indigo chip background
                            color: '#283593', // Darker indigo text
                            fontWeight: 500
                          }}
                        />
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails>
                      <Box sx={{ p: 3, backgroundColor: "#fafbff" }}> {/* Very light indigo background */}
                        <Box sx={{ 
                          p: 3, 
                          textAlign: 'center', 
                          backgroundColor: '#f3e5f5', // Light indigo/purple background
                          borderRadius: '12px',
                          border: '2px dashed #c5cae9' // Light indigo border
                        }}>
                          <Box sx={{
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            backgroundColor: '#e1f5fe', // Very light indigo background
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 2
                          }}>
                            <AssignmentIcon sx={{ fontSize: '2rem', color: '#3f51b5' }} /> {/* Indigo icon */}
                          </Box>
                          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                            No claims mapping policy assigned
                          </Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                            {(modifiedApp?.api?.oauth2PermissionScopes && modifiedApp.api.oauth2PermissionScopes.length > 0) ? (
                              "This API application could benefit from a claims mapping policy if you need to include custom claims in tokens issued to clients."
                            ) : (
                              "For most client applications, optional claims (above) are sufficient. Claims mapping policies are typically only needed for APIs or applications with specific custom claim requirements."
                            )}
                          </Typography>
                          <Box sx={{ 
                            mt: 2, 
                            p: 1.5, 
                            backgroundColor: '#e8eaf6', // Light indigo info background
                            borderRadius: '8px',
                            border: '1px solid #c5cae9', // Light indigo border
                            textAlign: 'center'
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <InfoOutlinedIcon sx={{ color: '#3f51b5', mr: 1.5, fontSize: '1rem' }} /> {/* Indigo info icon */}
                              <Typography variant="body2" color="text.secondary">
                                To manage Claims Mapping Policies, navigate to the <strong>Tools</strong> section in the main navigation 
                                and select <strong>Claims Mapping Policy Tool</strong>.
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </AccordionDetails>

                  </Accordion>
                )}
              </Box>

              {/* 2) About Claims Mapping Policy Section - Enhanced Card Design */}
              <Box sx={{ 
                m: 2, 
                borderRadius: '8px', // Smaller radius for info section
                overflow: 'hidden',
                boxShadow: '0 1px 4px rgba(0, 0, 0, 0.04)', // Lighter shadow
                opacity: 0.85 // De-emphasized
              }}>
                <Accordion defaultExpanded={false} sx={{
                  '&:before': { display: 'none' },
                  borderRadius: '8px !important'
                }}>
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ fontSize: '1.2rem', color: '#9e9e9e' }} />}
                    sx={{
                      background: '#f8f9fa', // Simple gray background
                      borderBottom: '1px solid #e9ecef',
                      minHeight: '48px', // Shorter height
                      '&:hover': {
                        backgroundColor: '#f1f3f4'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <InfoOutlinedIcon sx={{ mr: 1.5, fontSize: "1.1rem", color: 'info.main' }} /> {/* Updated to match */}
                      <Typography 
                        variant="subtitle1"
                        sx={{ 
                          fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                          fontSize: "1rem", 
                          fontWeight: 500,
                          letterSpacing: "0.005em"
                        }}
                      >
                        About Claims Mapping Policy
                      </Typography>
                      <Chip 
                        size="small"
                        label="Info" 
                        variant="outlined" 
                        sx={{ 
                          ml: 2,
                          borderColor: '#dee2e6',
                          color: '#6c757d',
                          fontSize: '0.65rem', 
                          height: '20px', 
                          fontWeight: 400
                        }}
                      />
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Box sx={{ p: 2, backgroundColor: "#f0f3ff", borderRadius: 1 }}> {/* Bluer tint than Optional Claims */}
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        Claims Mapping Policies define custom claims that are included in tokens issued to specific applications. 
                        Unlike Optional Claims which are configured per application, these policies can be reused across multiple applications.
                      </Typography>

                      {/* Added section for application types */}
                      <Box sx={{ 
                        p: 2, 
                        backgroundColor: '#e8eaf6', 
                        border: '1px solid #c5cae9',
                        borderRadius: 1,
                        mb: 2
                      }}>
                        <Typography variant="subtitle2" sx={{ color: '#303f9f', mb: 1 }}>
                          When to Use Claims Mapping Policies:
                        </Typography>
                        <Typography variant="body2">
                          <strong>Most commonly used for:</strong><br />
                          • <strong>API/Resource Applications</strong> that need to receive custom claims about users<br />
                          • <strong>Enterprise Applications</strong> integrated with SAML-based systems<br />
                          • <strong>Multi-tenant Applications</strong> that need tenant-specific claims<br />
                          • <strong>Legacy Applications</strong> requiring claims that don't match standard OIDC/OAuth formats
                        </Typography>
                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="body2">
                          <strong>Typically not needed for:</strong><br />
                          • <strong>Client Applications</strong> that only consume APIs (use Optional Claims instead)<br />
                          • <strong>Simple Single-tenant Applications</strong> with standard claim requirements<br />
                          • <strong>Mobile Applications</strong> that only need basic user information
                        </Typography>
                      </Box>                      
                      
                      {/* Visual explanation of claims mapping policies */}
                      <Box sx={{ 
                        p: 2, 
                        backgroundColor: '#ffffff', 
                        border: '1px solid #d4d9ff', // More bluish border
                        borderRadius: 1,
                        mb: 2
                      }}>
                        <Typography variant="subtitle2" sx={{ color: '#3949ab', mb: 1 }}>
                          Key Characteristics:
                        </Typography>
                        <Typography variant="body2">
                          • <strong>Centralized Management:</strong> Define once, apply to many applications<br />
                          • <strong>Custom Attribute Mapping:</strong> Map directory attributes to custom claim names<br />
                          • <strong>Token Customization:</strong> Extend tokens with organization-specific claims<br />
                          • <strong>Enhanced Security:</strong> Control which applications receive specific claims
                        </Typography>
                      </Box>
                      
                      <Box sx={{
                        p: 2,
                        backgroundColor: '#fff8e1',
                        border: '1px solid #ffe082',
                        borderRadius: 1
                      }}>
                        <Typography variant="subtitle2" sx={{ color: '#bf360c', mb: 1 }}>
                          Important Considerations:
                        </Typography>
                        <Typography variant="body2">
                          • Claims defined here take precedence over optional claims if naming conflicts exist<br />
                          • Changes to policies affect all applications to which they are assigned<br />
                          • Policies can contain both JWT and SAML claims, but only relevant ones are included based on the protocol used<br />
                          • The BasicClaimSet option controls whether standard claims are included in tokens
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionDetails>

                </Accordion>
              </Box>

            </Box>
          </AccordionDetails>
        </Accordion>
              
      </CardContent>
    </Card>
  );
};

export default OptionalClaims;
